import { RecreateTransformWorkerFlareTimeoutMS, RecreateTransformWorkerTimeoutMS } from '@common/collab/constants/collab-doc-worker-timeouts.constant';
import { WorkerTimeoutName } from '@common/workers/constants/worker-timeouts.constant';
import { DocStepsRecreatorResult, DocStepsRecreatorRunMessage } from '@portal-core/code-editor/types/doc-steps-recreator.type';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { TaskWorkerBase } from '@portal-core/util/task-worker.base';

export class FlareDocStepsRecreatorTaskWorker extends TaskWorkerBase<DocStepsRecreatorResult> {
  constructor() {
    super('FlareDocStepsRecreatorTaskWorker', () => {
      /*
       * Bundlers, including Webpack, Vite, and Parcel, recommend passing URLs that are resolved relative to import.meta.url to the Worker() constructor.
       * This way, the path is relative to the current script instead of the current HTML page, which allows the bundler to safely do optimizations like renaming.
       * Because otherwise the worker.js URL may point to a file not controlled by the bundler, so it cannot make any assumptions.
       */
      return new Worker(new URL('@portal-core/code-editor/workers/flare-doc-steps-recreator.worker', import.meta.url));
    });
  }

  /**
   * Runs the doc-steps-recreator web worker with the provided message.
   * For flare docs, it will try the RFC6902 JSON Patcher first, and if that times out, it will try the fast-json-patch patcher.
   * @param message The message to send to the web worker.
   * @param timeoutMS Optional timeout in milliseconds for the worker to complete.
   * @returns A promise that resolves with the result of the worker.
   */
  public run<T>(message: T, timeoutMS?: number): Promise<DocStepsRecreatorResult> {
    // Type assertion is safe because this worker expects DocStepsRecreatorRunMessage
    let msg = message as DocStepsRecreatorRunMessage;

    // The timeout for the fast-json-patch fallback run: the caller's timeout when supplied, otherwise the
    // default (longer) worker timeout — not the short Flare timeout used only to detect a slow rfc6902 attempt.
    const fallbackTimeoutMS = typeof timeoutMS === 'number' ? timeoutMS : RecreateTransformWorkerTimeoutMS;

    // If the file type is Flare
    if (msg.fileType === ProjectFileType.Flare) {
      // Ensure the options are set to use 'rfc6902' for the first run
      msg = {
        ...msg,
        options: {
          ...msg.options,
          jsonPatcher: 'rfc6902'
        }
      };

      // If no timeout is specified, use the Flare specific worker timeout
      if (typeof timeoutMS !== 'number') {
        timeoutMS = RecreateTransformWorkerFlareTimeoutMS;
      }
    } else {
      // If no timeout is specified, use the default worker timeout
      if (typeof timeoutMS !== 'number') {
        timeoutMS = RecreateTransformWorkerTimeoutMS;
      }
    }

    return super.run(msg, timeoutMS).catch(error => {
      // Fallback to a 'fast-json-patch' recreation if a timeout occurs on Flare documents. The fallback run is
      // bounded by its own timeout so a stuck fallback worker can't hang forever.
      if (msg.fileType === ProjectFileType.Flare && error.name === WorkerTimeoutName) {
        console.log(`[${this.name}] worker timeout. Using fallback with 'fast-json-patch'.`, error);
        msg.options.jsonPatcher = 'fast-json-patch';
        return super.run(msg, fallbackTimeoutMS);
      } else {
        throw error;
      }
    });
  }
}
